import { useEffect, useRef, useState } from 'react'
import { useParams, useSearch } from '@tanstack/react-router'
import { router as tanstackRouter } from '@/router'
import { connectToChild } from 'penpal'
import {isApp, openLink, storage} from '@/helpers'
import Loading from '@/components/Loading'
import { webrtc } from '@/providers/MfdProvider'
import { PeerEvents } from '@gameglass/webrtc'
import { DATA_TYPE } from '@gameglass/shared'
import { LAST_SHARD_NAME } from "@/config/settings"
import { FORGE_URL } from "@/constants"
import {DATA_ACTION_TYPES} from "@gameglass/shared";
import {isFullscreenEnabled, useFullscreen} from "@/hooks/useFullscreen";

export let penpal: ReturnType<typeof connectToChild> | null = null

let edState: any = undefined

const ShardIframe = () => {
	const iframeRef = useRef<HTMLIFrameElement>(null)
	const { shardId, collectionId } = useParams({ strict: false })
	const { sourceShardId } = useSearch({ strict: false }) as { sourceShardId?: string }
	const [loading, setLoading] = useState(true)
	const token = storage.getItem('token')
	const showIframe = !!shardId && !!token
	const [iframeSource, setIframeSource] = useState<string | undefined>()
	const { isFullscreen, setFullscreen } = useFullscreen()

	useEffect(() => {
		const sendData = async ({ data }: { data: any }) => {

			if (!data?.payload || !penpal) {
				return
			}

			const connection = await penpal.promise

			switch (data?.type) {
				case DATA_TYPE.eliteDangerous: {
					try {
						edState = {
							...(edState || {}),
							...data.payload
						}
						void (connection as any)?.edState(data.payload)
					} catch (error) {
						console.warn(error)
					}
				}
					break
				case DATA_TYPE.hubData: {
					// Never wait for data
					void (connection as any)?.sendHubData(data.payload)
				}
					break

				case DATA_ACTION_TYPES.hubInfo:
					void (connection as any)?.sendHubInfo(data.payload)
					break
				default:
					void (connection as any)?.sendAction(data.payload)
					break
			}
		}

		webrtc.on(PeerEvents.data, sendData)

		return () => {
			webrtc.off(PeerEvents.data, sendData)
		}
	}, [])

	useEffect(() => {
		// Only run it when all information is available, but only once at first load.
		if (!shardId || !token || iframeSource) return

		setLoading(true)

		const urlSearchParams = new URLSearchParams({
			authToken: token,
			t: String(Date.now())
		})

		if (shardId) urlSearchParams.set('shardId', shardId)
		if (sourceShardId) urlSearchParams.set('sourceShardId', sourceShardId)

		setIframeSource(`${FORGE_URL}/render?${urlSearchParams}`)
	}, [token, shardId, sourceShardId])

	useEffect(() => {
		// Prevent penpal from being initialized more than once
		if (!iframeRef.current || !iframeSource || penpal) return

		try {
			penpal = connectToChild({
				iframe: iframeRef.current,
				methods: {
					clientMounted: (isMounted: boolean) => {
						setLoading(!isMounted)
						if (isMounted && edState) {
							penpal?.promise.then((connection: any) => connection.edState(edState))
						}
					},
					goToForge: (where: string) => {
						// Change route on next frame to prevent gesture deadlocking ios bug
						setTimeout(() => tanstackRouter.navigate({
							to: where === 'collection_shard_list' ?
								'/shardlibrary' :
								`/shardlibrary/${collectionId || 0}`
						}), 0)
					},
					goToURL: (url: string, useDefaultBrowser: boolean) => openLink(url, useDefaultBrowser),
					rtcSend: (data: any) => {
						webrtc?.send?.(data)
					},
					rtcSendWhenConnected: (data: any) => {
						webrtc?.sendWhenConnected?.(data)
					},
					setShardName: (name: string) => {
						storage.setItem(LAST_SHARD_NAME, name)
					},
					changeRoute: ({ collectionId: destCollectionId, shardId: destShardId, sourceShardId: destSourceShardId }: { collectionId: string; shardId: string; sourceShardId?: string }) => {
						const path = `/shardlibrary/${destCollectionId}/${destShardId}${destSourceShardId ? `?sourceShardId=${destSourceShardId}` : ''}`
						const currentHref = tanstackRouter.state.location.href

						if (path !== currentHref) {
							tanstackRouter.navigate({ to: path })
						}
					},
					getHubInfo: () => {
						webrtc?.sendWhenConnected?.({ type: DATA_ACTION_TYPES.hubInfo })
					},
					getFullscreen: () => {
						penpal?.promise.then((connection: any) => connection.setFullscreen(isFullscreen, isFullscreenEnabled))
					},
					setFullscreen: (value = false) => {
						void setFullscreen(value)
					}
				}
			})
		} catch (error) {
			console.warn(error)
		}
	}, [iframeRef.current, iframeSource])

	useEffect(() => {
		// When iframe is not shown, reset all connections
		if (!showIframe && iframeSource) {
			// Unsubscribe from any variables the previous shard had
			webrtc?.send?.({
				type: DATA_ACTION_TYPES.unsubscribeData,
			})
			setIframeSource(undefined)
			penpal?.destroy()
			penpal = null
		}
	}, [showIframe])

	useEffect(() => {
		penpal?.promise.then((connection: any) => connection.setFullscreen(isFullscreen))
	}, [isFullscreen])

	return (
		<>
			{showIframe && loading && <Loading message="Loading shard..." />}
			<iframe
				ref={iframeRef}
				allowFullScreen={!isApp()}
				style={{
					display: showIframe ? 'block' : 'none',
					position: 'fixed',
					width: '100%',
					height: '100%'
				}}
				src={iframeSource}
			/>
		</>
	)
}

const ShardIframeComponent = () => {
	return (
		<ShardIframe />
	)
}

export default ShardIframeComponent
